// What a document — or a run — is made OF: a charge for something we sell, a
// cost of ours coming back, or both.
//
// This is the distinction every billing surface in the app depends on and none
// of them used to draw. The two halves of the system have always been separate
// kinds of clause — a `revenue` term line and a `pass_through_cost` one — and
// they meet on an invoice, where they became one number: a Kingsport expense
// recovery and a Kingsport subscription bill were two rows that differed only
// in the figure on them. They are not the same money —
// one is what we charge for our service, the other is spend we are getting back
// at the agreed share — and a screen for deciding what to do next has to say
// which it is holding.
//
// ONE MODULE FOR THREE SURFACES, exactly as `SplitSource` is one module for
// four: the dashboard, the invoice list and the invoice sheet all answer this
// question, and three copies of the vocabulary is how they come to answer it
// differently.
//
// THE COLOURS ARE NOT NEW. /calendar has paired `bill_due` with brand and
// `cost_bill_due` with teal since it was written (`calendarEventKindTone`), and
// the same pairing here means a reader who has learned the calendar has already
// learned this. Colour is never the only cue — every badge says its kind in
// words, and the amounts beside it are labelled.
//
// A THIRD KIND THAT IS NEITHER. A standalone line — a credit, a discount, a
// true-up — points at no term line, so it is neither a charge nor a recovery;
// it is a correction to the document, and `InvoiceLine.category` is what says
// which. To fold it into either half would make a credit note read as revenue,
// so it gets its own word.

import type { ReactNode } from "react";
import { Badge, type Tone } from "../design/Badge.js";

/**
 * The four answers, in the order of how much they change what a reader does.
 *
 * `mixed` is its own case rather than "whichever there is more of": a document
 * carrying both is the one where the total on its own is most misleading, and
 * rounding it to the larger half would hide exactly that.
 */
export type ChargeMix = "bill" | "cost_recharge" | "mixed" | "correction";

/** The line counts any of the three surfaces can produce, by whatever name. */
export interface ChargeMixCounts {
  bills: number;
  recharges: number;
  /** Lines pointing at neither. Absent on a run, which has no such line. */
  standalone?: number;
}

/**
 * What a set of lines adds up to — or null when there are no lines to describe.
 *
 * Null rather than a fourth word for "empty": a draft with nothing on it yet is
 * not a KIND of document, and a badge reading "Empty" would occupy the place
 * where the answer goes while answering nothing. The caller renders no badge.
 */
export function chargeMix(counts: ChargeMixCounts): ChargeMix | null {
  const standalone = counts.standalone ?? 0;
  if (counts.bills === 0 && counts.recharges === 0) {
    return standalone === 0 ? null : "correction";
  }
  if (counts.bills > 0 && counts.recharges > 0) return "mixed";
  return counts.recharges > 0 ? "cost_recharge" : "bill";
}

const LABEL: Record<ChargeMix, string> = {
  bill: "Billed",
  cost_recharge: "Cost recovery",
  mixed: "Mixed",
  correction: "Correction",
};

const TONE: Record<ChargeMix, Tone> = {
  bill: "brand",
  cost_recharge: "teal",
  // Neither of the two, because it is both — and the one a reader most needs to
  // stop at, since its total is the one that describes nothing on its own.
  mixed: "info",
  correction: "slate",
};

/**
 * Why the badge says what it says, in the `title`, because the word alone is
 * the shortest true answer and not always the whole one. "Mixed" in particular
 * is useless without the counts.
 */
function describe(mix: ChargeMix, counts: ChargeMixCounts): string {
  const standalone = counts.standalone ?? 0;
  switch (mix) {
    case "bill":
      return "Charges for what we sell. Nothing on this is a cost coming back.";
    case "cost_recharge":
      return "Costs of ours coming back at the agreed share — money already spent, not money earned.";
    case "mixed":
      return (
        `${String(counts.bills)} charged for what we sell, ` +
        `${String(counts.recharges)} recovering costs of ours` +
        `${standalone > 0 ? `, ${String(standalone)} correcting the document` : ""}. ` +
        `The total covers both.`
      );
    case "correction":
      return "Credits, discounts and true-ups only — lines that point at no bill and no cost.";
  }
}

/**
 * The badge itself. `shape` is the caller's, because a table cell and a
 * document header want different weights of the same word.
 */
export function ChargeMixBadge({
  counts,
  shape = "tag",
}: {
  counts: ChargeMixCounts;
  shape?: "chip" | "tag";
}): ReactNode {
  const mix = chargeMix(counts);
  if (mix === null) return null;
  return (
    <span title={describe(mix, counts)}>
      <Badge tone={TONE[mix]} shape={shape}>
        {LABEL[mix]}
      </Badge>
    </span>
  );
}

/** Exported for the surfaces that need the word without the badge. */
export function chargeMixLabel(mix: ChargeMix): string {
  return LABEL[mix];
}
